import React, { useState, useEffect } from 'react';
import { useActions } from 'hooks/useActions';
import { useSelector } from 'react-redux';
import {
  Typography,
  Box,
  MuiBox,
  Card,
  SvgIconButton,
  FlexBox,
  MuiTable,
  SelectDropdown,
  SvgIcon,
  Tooltip
} from '@kis-technologies/kis-ui';
import { ConfigMappingProps, MappingRelation } from '../../types';
import { useStyles } from '../../styled';
import { MappingEditDrawer } from '../MappingEditDrawer';
import { getFormattedValue } from '../../utils';
import { ActionOverlay } from 'components/ActionOverlay';
import { selectors as kioskSelectors, Kiosk } from 'store/kiosks';
import { Hardware } from 'store/hardware';
import {
  actions as kioskHardwareActions,
  selectors as kioskHardwareSelectors,
  KioskHardware
} from 'store/kioskHardware';
import {
  actions as kioskTicketTemplateActions,
  selectors as kioskTicketTemplateSelectors,
  KioskTicketTemplate
} from 'store/kioskTicketTemplates';
import { ConfigCreateOverlay } from '../../components/ConfigCreateOverlay';
import { getSortedList } from 'utils/stringFormat';

export const ConfigMapping = (props: ConfigMappingProps) => {
  const { mappingData, mappingType, columns, onDeleteConfirm } = props;
  const kiosks = useSelector(kioskSelectors.selectKiosks);
  const kioskHardware = useSelector(kioskHardwareSelectors.selectKioskHardware);
  const kioskTicketTemplates = useSelector(kioskTicketTemplateSelectors.selectKioskTicketTemplates);
  const [selectedKiosk, setSelectedKiosk] = useState<Kiosk | null>(null);
  const [selectedRelation, setSelectedRelation] = useState<MappingRelation | null>(null);
  const [showCreateOverlay, setShowCreateOverlay] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showDeleteOverlay, setShowDeleteOverlay] = useState(false);
  const classes = useStyles({ showDrawer })();

  const [getKioskHardware, resetKioskHardware, resetKioskHardwareAction] = useActions([
    kioskHardwareActions.getKioskHardware,
    kioskHardwareActions.resetKioskHardware,
    kioskHardwareActions.resetAction
  ]);

  const [getKioskTicketTemplates, resetKioskTicketTemplates, resetKioskTicketTemplateAction] = useActions([
    kioskTicketTemplateActions.getKioskTicketTemplates,
    kioskTicketTemplateActions.resetKioskTicketTemplates,
    kioskTicketTemplateActions.resetAction
  ]);

  const { success: kioskHardwareSuccess } = useSelector(kioskHardwareSelectors.selectAction);
  const { success: kioskTicketTemplateSuccess } = useSelector(kioskTicketTemplateSelectors.selectAction);

  useEffect(() => {
    resetKioskHardware();
    resetKioskTicketTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedRelation) {
      const updatedKioskMapping = kioskHardware.find((kh) => kh.id === selectedRelation.id);
      if (updatedKioskMapping) {
        setSelectedRelation(updatedKioskMapping);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskHardware]);

  useEffect(() => {
    if (selectedRelation) {
      const updatedKioskMapping = kioskTicketTemplates.find((ktt) => ktt.id === selectedRelation.id);
      if (updatedKioskMapping) {
        setSelectedRelation(updatedKioskMapping);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskTicketTemplates]);

  useEffect(() => {
    if (kioskHardwareSuccess && selectedKiosk) {
      getKioskHardware({ kioskId: selectedKiosk.id });
      resetKioskHardwareAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskHardwareSuccess]);

  useEffect(() => {
    if (kioskTicketTemplateSuccess && selectedKiosk) {
      getKioskTicketTemplates({ kioskId: selectedKiosk.id });
      resetKioskTicketTemplateAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskTicketTemplateSuccess]);

  const onKioskSelect = (selected: Kiosk) => {
    setSelectedKiosk(selected);
    if (mappingType === 'kioskHardware') {
      resetKioskHardwareAction();
      getKioskHardware({ kioskId: selected.id });
    } else if (mappingType === 'kioskTicketTemplate') {
      resetKioskTicketTemplateAction();
      getKioskTicketTemplates({ kioskId: selected.id });
    }
  };

  const onEditClick = (selected: MappingRelation) => {
    setSelectedRelation(selected);
    setShowDrawer(true);
  };

  const onDeleteConfigClick = (selected: MappingRelation) => {
    setSelectedRelation(selected);
    setShowDeleteOverlay(true);
  };

  const onConfigDeleteConfirm = () => {
    const payload = {
      id: selectedRelation && selectedRelation.id,
      kioskId: selectedRelation && selectedRelation.kioskId
    };
    onDeleteConfirm(mappingType, payload);
    setShowDeleteOverlay(false);
  };

  const resetOverlay = () => {
    setShowDrawer(false);
    setShowDeleteOverlay(false);
  };

  const getName = (mappingRelation: MappingRelation): string => {
    let displayName = '';
    if (mappingType === 'kioskHardware') {
      const relationId = (mappingRelation as KioskHardware).hardwareId;
      const hardware = mappingData.find((h) => h.id === relationId);
      if (hardware) {
        displayName = `${hardware.name} (${hardware.type})`;
      }
    } else if (mappingType === 'kioskTicketTemplate') {
      const relationId = (mappingRelation as KioskTicketTemplate).ticketTemplateId;
      displayName = relationId;
      const ticketTemplate = mappingData.find((tt) => tt.id === relationId);
      if (ticketTemplate) {
        displayName = `${ticketTemplate.id} (${ticketTemplate.type})`;
      }
    }
    return displayName;
  };

  const selectedHardware = () => {
    let item;
    if (mappingType === 'kioskHardware') {
      const relationId = (selectedRelation as KioskHardware).hardwareId;
      item = mappingData.find((hardware) => hardware.id === relationId);
    }
    return item;
  };

  return (
    <>
      <FlexBox direction="column" flexOverride="1 1 auto" sx={{ minWidth: '100%' }}>
        <FlexBox direction="row" margin="0 0 6 0" fullWidth align="center">
          <Typography fontSize={24} fontWeight={700} sx={{ color: 'black' }}>
            Kiosk
          </Typography>
          <Box width={64} />
          <SelectDropdown
            items={getSortedList(kiosks, 'name')}
            itemToString={(item: Kiosk) => item && item.name}
            selectedItem={selectedKiosk as Kiosk}
            onChange={(kiosk: Kiosk) => onKioskSelect(kiosk)}
            placeholder="Select a kiosk"
            data-testid="media-selector"
          />
        </FlexBox>
        {/* TABLE */}
        <MuiBox className={classes.configMappingTableOuterBox}>
          <Card hasBorderRadius raised fullHeight sx={{ width: '100%' }}>
            <MuiTable.TableContainer id="userViewTable" className={classes.configTableContainer}>
              <MuiTable.Table stickyHeader sx={{ borderCollapse: 'collapse', width: '100%' }}>
                <MuiTable.TableHead>
                  <MuiTable.TableRow className={classes.configMappingTableRows}>
                    {columns &&
                      columns.sort().map((column) => {
                        return (
                          <MuiTable.TableCell
                            className={classes.configMappingHeadersProperty}
                            sx={{ width: column.width ? column.width : 'fit-content' }}
                          >
                            {' '}
                            {column.displayName}
                          </MuiTable.TableCell>
                        );
                      })}
                  </MuiTable.TableRow>
                </MuiTable.TableHead>

                <MuiTable.TableBody>
                  {kioskHardware &&
                    kioskHardware.sort().map((kh) => {
                      return (
                        <MuiTable.TableRow className={classes.configMappingTableRows}>
                          <MuiTable.TableCell>
                            <Typography fontSize={16}>{getName(kh)}</Typography>
                          </MuiTable.TableCell>
                          <MuiTable.TableCell className={classes.configMappingTableValueCell}>
                            <Typography fontSize={16}>{getFormattedValue(kh.configuration)}</Typography>
                          </MuiTable.TableCell>
                          <MuiTable.TableCell className={classes.configMappingTableValueCell}>
                            <Typography fontSize={16}>{getFormattedValue(kh.values)}</Typography>
                          </MuiTable.TableCell>
                          <MuiTable.TableCell className={classes.configMappingTableValueCell}>
                            <Typography fontSize={16}>{getFormattedValue(kh.serialCode)}</Typography>
                          </MuiTable.TableCell>
                          <MuiTable.TableCell className={classes.configMappingTableValueCell}>
                            <Typography fontSize={16}>{getFormattedValue(kh.status)}</Typography>
                          </MuiTable.TableCell>
                          <MuiTable.TableCell>
                            <FlexBox align="stretch" fullWidth>
                              <SvgIconButton
                                onClick={() => onDeleteConfigClick(kh)}
                                data-testid="delete-config-button"
                                style={{ marginRight: '15px' }}
                              >
                                <SvgIcon name="exit_circle" />
                              </SvgIconButton>
                              <SvgIconButton onClick={() => onEditClick(kh)} data-testid="update-user-button">
                                <SvgIcon name="edit_circle" />
                              </SvgIconButton>
                            </FlexBox>
                          </MuiTable.TableCell>
                        </MuiTable.TableRow>
                      );
                    })}
                  {kioskTicketTemplates &&
                    kioskTicketTemplates.sort().map((kioskTicketTemplate) => {
                      return (
                        <MuiTable.TableRow className={classes.configMappingTableRows}>
                          <MuiTable.TableCell>
                            <Typography fontSize={16}>{getName(kioskTicketTemplate)}</Typography>
                          </MuiTable.TableCell>
                          <MuiTable.TableCell>
                            <FlexBox align="stretch" fullWidth>
                              <SvgIconButton
                                onClick={() => onDeleteConfigClick(kioskTicketTemplate)}
                                data-testid="delete-config-button"
                                style={{ marginRight: '15px' }}
                              >
                                <SvgIcon name="exit_circle" />
                              </SvgIconButton>
                            </FlexBox>
                          </MuiTable.TableCell>
                        </MuiTable.TableRow>
                      );
                    })}
                </MuiTable.TableBody>
              </MuiTable.Table>
            </MuiTable.TableContainer>
          </Card>
          {selectedKiosk && (
            <MuiBox height="70px" sx={{ padding: '16px' }}>
              <Tooltip title="Add Config" arrow placement={'top'} verticalOffset={-8}>
                <SvgIconButton onClick={() => setShowCreateOverlay(true)}>
                  <SvgIcon name="plus_circle" />
                </SvgIconButton>
              </Tooltip>
            </MuiBox>
          )}
        </MuiBox>
      </FlexBox>

      {selectedKiosk && showCreateOverlay && (
        <ConfigCreateOverlay
          kiosk={selectedKiosk}
          configType={mappingType}
          onCancel={() => setShowCreateOverlay(false)}
        />
      )}

      {showDrawer && (
        <MappingEditDrawer
          onClose={resetOverlay}
          isOpen={showDrawer}
          configData={selectedRelation as KioskHardware}
          configItem={selectedHardware() as Hardware}
          kiosk={selectedKiosk as Kiosk} // Always there will be a selectedKiosk
        />
      )}

      {showDeleteOverlay && (
        <ActionOverlay
          title={
            <MuiBox className={classes.configDeleteHeading}>
              <Typography className={classes.configDeleteHeader} sx={{ lineHeight: 1, fontWeight: 600 }}>
                Are you sure you want to delete?
              </Typography>
            </MuiBox>
          }
          confirmText="Delete"
          onConfirm={() => onConfigDeleteConfirm()}
          cancelText="Back"
          onCancel={resetOverlay}
          width={800}
          appearance="error"
        />
      )}
    </>
  );
};
